// Wheel slice colors, shared by the pie chart and the Activity Tracking buttons so
// each wellness dimension shows the same color everywhere.
//
// Kept out of charts/WellnessPieChart so Dashboard can use it without pulling
// recharts into the resident bundle (the chart is lazy-loaded).

export interface ColorableDimension {
  id: string;
  name: string;
  color: string;
  categoryId?: string | null;
  categoryColor?: string;
}

// Same ordering the pie uses: grouped by category, then by name, uncategorized last.
export function compareDimensions(a: ColorableDimension, b: ColorableDimension): number {
  if (a.categoryId && b.categoryId) {
    if (a.categoryId !== b.categoryId) {
      return a.categoryId.localeCompare(b.categoryId);
    }
    return a.name.localeCompare(b.name);
  }
  if (a.categoryId && !b.categoryId) return -1;
  if (!a.categoryId && b.categoryId) return 1;
  return a.name.localeCompare(b.name);
}

/** Lighten a #rrggbb color toward white by `amount` (0..1). Invalid input returned as-is. */
export function lightenColor(hex: string, amount: number): string {
  const m = (hex || '').trim().match(/^#([0-9a-f]{6})$/i);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const chan = (shift: number) => {
    const c = (n >> shift) & 0xff;
    return Math.round(c + (255 - c) * Math.max(0, Math.min(1, amount)));
  };
  return `#${[16, 8, 0].map(s => chan(s).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Slice color per dimension id. With useCategoryColors, dimensions inherit their
 * category's color: the first dimension in a category gets the exact category color
 * (matching the legend chip) and each subsequent one a progressively lighter shade,
 * so the wheel reads as one color family per legend entry. Dimensions without a
 * category (or with the setting off) keep their own configured color.
 *
 * Shades are assigned over every dimension, with or without data, so a dimension's
 * color doesn't shift when a neighbor's slice appears (the wheel now updates live
 * after an activity is logged) and matches the Activity Tracking buttons.
 */
export function computeSliceColors(
  dimensions: ColorableDimension[],
  useCategoryColors: boolean
): Map<string, string> {
  const map = new Map<string, string>();
  const perCategoryIndex = new Map<string, number>();
  const sorted = [...dimensions].sort(compareDimensions);
  for (const dim of sorted) {
    if (useCategoryColors && dim.categoryId && dim.categoryColor) {
      const i = perCategoryIndex.get(dim.categoryId) ?? 0;
      perCategoryIndex.set(dim.categoryId, i + 1);
      map.set(dim.id, lightenColor(dim.categoryColor, Math.min(i * 0.22, 0.66)));
    } else {
      map.set(dim.id, dim.color);
    }
  }
  return map;
}

/** Return the dimensions with `color` replaced by their wheel slice color. */
export function withWheelColors<T extends ColorableDimension>(
  dimensions: T[],
  categories: { id: string; color: string }[],
  useCategoryColors: boolean
): T[] {
  const categoryColorById = new Map(categories.map(c => [c.id, c.color]));
  const withCategoryColor = dimensions.map(d => ({
    ...d,
    categoryColor: d.categoryColor ?? (d.categoryId ? categoryColorById.get(d.categoryId) : undefined),
  }));
  const colors = computeSliceColors(withCategoryColor, useCategoryColors);
  return dimensions.map(d => ({ ...d, color: colors.get(d.id) ?? d.color }));
}
